import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Sparkles, RefreshCw, X } from "lucide-react";
import useVersionCheck from "@/hooks/useVersionCheck";
import "./NewVersionBanner.css";

export const SNOOZE_KEY = "elelem_version_banner_snoozed_until";
export const SNOOZE_DURATION_MS = 60 * 60 * 1000; // 1 hour

const getInitialSnooze = () => {
  try {
    const val = Number(sessionStorage.getItem(SNOOZE_KEY) || 0);
    return val > Date.now() ? val : 0;
  } catch {
    return 0;
  }
};

/** Compact, Linear-style non-blocking prompt shown when a newer build is deployed. */
export default function NewVersionBanner() {
  const { t } = useTranslation();
  const { updateAvailable } = useVersionCheck();
  const [snoozedUntil, setSnoozedUntil] = useState(getInitialSnooze);
  const [isReloading, setIsReloading] = useState(false);

  useEffect(() => {
    if (!snoozedUntil) return undefined;

    const checkSnoozeExpired = () => {
      if (Date.now() >= snoozedUntil) {
        setSnoozedUntil(0);
        try {
          sessionStorage.removeItem(SNOOZE_KEY);
        } catch {}
      }
    };

    const remaining = Math.max(0, snoozedUntil - Date.now());
    const timer = setTimeout(checkSnoozeExpired, remaining);
    window.addEventListener("focus", checkSnoozeExpired);
    document.addEventListener("visibilitychange", checkSnoozeExpired);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("focus", checkSnoozeExpired);
      document.removeEventListener("visibilitychange", checkSnoozeExpired);
    };
  }, [snoozedUntil]);

  const handleDismiss = () => {
    const until = Date.now() + SNOOZE_DURATION_MS;
    setSnoozedUntil(until);
    try {
      sessionStorage.setItem(SNOOZE_KEY, String(until));
    } catch {}
  };

  const handleRefresh = () => {
    setIsReloading(true);
    window.location.reload();
  };

  const isSnoozed = snoozedUntil > 0 && Date.now() < snoozedUntil;

  if (!updateAvailable || isSnoozed) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="new-version-banner"
      className="elelem-update-toast"
    >
      <div className="elelem-update-toast-icon" aria-hidden="true">
        {isReloading ? (
          <RefreshCw size={14} className="elelem-update-toast-spin" />
        ) : (
          <Sparkles size={14} />
        )}
      </div>
      <div className="elelem-update-toast-content">
        <div className="elelem-update-toast-title">
          {t("newVersion.title", "Update available")}
        </div>
        <div className="elelem-update-toast-desc">
          {t("newVersion.description", "Refresh to get the latest version.")}
        </div>
        <button
          type="button"
          className="elelem-update-toast-refresh-btn"
          onClick={handleRefresh}
          disabled={isReloading}
          data-testid="new-version-refresh-btn"
        >
          {t("newVersion.refresh", "Refresh")}
        </button>
      </div>
      <button
        type="button"
        className="elelem-update-toast-close-btn"
        aria-label={t("newVersion.dismiss", "Dismiss")}
        onClick={handleDismiss}
      >
        <X size={14} />
      </button>
    </div>
  );
}
